/**
 * Chat History Search Hook
 *
 * Searches the user's own Bird conversations by message content. Mirrors
 * `useSearchQuery`'s shape — same debounce, same `keepPreviousData` so results
 * don't flash between keystrokes — because both feed the same command palette
 * and any difference in feel between the zones would read as a bug.
 *
 * Chat search is per-user by construction (the agent service scopes every query
 * to the caller's uid and workspace), so unlike entity search there is nothing
 * here to filter client-side.
 */

import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { searchChatHistory, type ChatSearchResult } from '../../services/agentChat.service';
import { useWorkspaceId } from '../../contexts/WorkspaceContext';
import { useDebounce } from '../ui/useDebounce';

/**
 * Workspace is part of the key even though the server scopes by it anyway:
 * without it, switching workspace would serve the previous one's cached chat
 * results until they went stale.
 */
export const chatSearchQueryKey = (workspaceId: string, query: string) =>
  ['chat-search', { workspaceId, query }] as const;

const DEBOUNCE_MS = 200;
const PAGE_SIZE = 20;

interface UseChatSearchQueryOptions {
  enabled?: boolean;
  limit?: number;
}

export function useChatSearchQuery(query: string, options?: UseChatSearchQueryOptions) {
  const workspaceId = useWorkspaceId();
  const debouncedQuery = useDebounce(query, DEBOUNCE_MS);

  const result = useQuery({
    queryKey: chatSearchQueryKey(workspaceId || '', debouncedQuery),
    queryFn: () => searchChatHistory(debouncedQuery, 0, options?.limit ?? PAGE_SIZE),
    enabled: !!workspaceId && debouncedQuery.trim().length > 0 && options?.enabled !== false,
    // Chat history only changes when the user sends a message, so a short
    // stale window costs nothing and keeps repeated palette opens instant.
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });

  const results: ChatSearchResult[] = result.data?.results ?? [];

  return {
    results,
    total: result.data?.total ?? 0,
    // Carried, not dropped. The server computes this and `agentChat.service`
    // sets it for a failed fetch — discarding it here made a search that could
    // not RUN render as a search that found NOTHING, which is the one outcome
    // the whole degrade path exists to prevent.
    degraded: result.data?.degraded === true,
    isLoading: result.isLoading,
    isFetching: result.isFetching,
    debouncedQuery,
  };
}
